import {
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Loader2,
    X,
} from 'lucide-react';
import { Fragment, useState } from 'react';

import { type ToolCardPresentation } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    type ToolApprovalDecision,
    type ToolApprovalGroup,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Button } from '@apps/web/components/ui/button';
import { cn } from '@apps/web/lib/utils';

function formatApprovalGroupValue(value: unknown): string {
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    if (value === null || value === undefined) {
        return 'unset';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

function formatApprovalGroupMode(mode: string): string {
    if (mode === 'live') {
        return 'Live';
    }
    if (mode === 'sandbox') {
        return 'Sandbox';
    }
    return mode
        .replace(/_/g, ' ')
        .replace(/^\w/, (char: string) => char.toUpperCase());
}

export function ToolApprovalGroupCard({
    group,
    isSubmitting,
    onDecision,
    presentation = 'default',
    approvalPosition,
    approvalCount,
    canGoToPreviousApproval = false,
    canGoToNextApproval = false,
    onPreviousApproval,
    onNextApproval,
}: {
    readonly group: ToolApprovalGroup;
    readonly isSubmitting: boolean;
    readonly onDecision?: (decision: ToolApprovalDecision) => Promise<void>;
    readonly presentation?: ToolCardPresentation;
    readonly approvalPosition?: number;
    readonly approvalCount?: number;
    readonly canGoToPreviousApproval?: boolean;
    readonly canGoToNextApproval?: boolean;
    readonly onPreviousApproval?: () => void;
    readonly onNextApproval?: () => void;
}) {
    const [showAllTargets, setShowAllTargets] = useState(false);
    const usesMinimalPresentation = presentation === 'minimal';
    const showApprovalNavigation =
        approvalPosition !== undefined &&
        approvalCount !== undefined &&
        approvalCount > 1;
    const targetCount = group.targetCount || group.targets.length;
    const visibleTargets = showAllTargets
        ? group.targets
        : group.targets.slice(0, 5);
    const hiddenTargetCount = Math.max(
        group.targets.length - visibleTargets.length,
        0,
    );
    const featureSummary = Object.entries(group.featureValues)
        .map(([key, value]) => `${key}=${formatApprovalGroupValue(value)}`)
        .join(', ');
    const canSubmit = !!onDecision && group.callIds.length > 0;

    const card = (
        <div
            className={cn(
                'flex w-full flex-col break-words',
                usesMinimalPresentation
                    ? 'max-w-[820px] overflow-hidden rounded-[8px] border border-[#DCDCDC] bg-white shadow-[0_10px_28px_rgba(0,0,0,0.06)]'
                    : 'border-border/50 bg-card max-w-2xl rounded-xl border p-4',
            )}
        >
            <div
                className={cn(
                    'flex items-start justify-between gap-3',
                    usesMinimalPresentation &&
                        'border-b border-[#E5E5E5] px-4 py-3',
                )}
            >
                <div className="min-w-0">
                    {usesMinimalPresentation ? (
                        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[#6B6B6B]">
                            <span className="size-1.5 rounded-full bg-amber-500" />
                            Batch tool approval
                        </div>
                    ) : null}
                    <p
                        className={cn(
                            'font-medium',
                            usesMinimalPresentation
                                ? 'text-[14px] text-[#1A1A1A]'
                                : 'text-foreground text-base',
                        )}
                    >
                        {group.title}
                    </p>
                    <p
                        className={cn(
                            'mt-1 leading-5',
                            usesMinimalPresentation
                                ? 'text-[13px] text-[#5A5A5A]'
                                : 'text-muted-foreground text-sm',
                        )}
                    >
                        Update {targetCount}{' '}
                        {targetCount === 1 ? 'organization' : 'organizations'}
                        {featureSummary ? ` with ${featureSummary}` : ''}.
                    </p>
                </div>
                {showApprovalNavigation ? (
                    <div
                        className={cn(
                            'flex shrink-0 items-center gap-2 text-sm',
                            usesMinimalPresentation
                                ? 'text-[#6B6B6B]'
                                : 'text-muted-foreground',
                        )}
                    >
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={cn(
                                'h-7 w-7',
                                usesMinimalPresentation &&
                                    'rounded-[6px] text-[#6B6B6B] hover:bg-[#F5F5F5] hover:text-[#1A1A1A]',
                            )}
                            disabled={!canGoToPreviousApproval || isSubmitting}
                            onClick={onPreviousApproval}
                            aria-label="Previous approval"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span>
                            {approvalPosition} of {approvalCount}
                        </span>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={cn(
                                'h-7 w-7',
                                usesMinimalPresentation &&
                                    'rounded-[6px] text-[#6B6B6B] hover:bg-[#F5F5F5] hover:text-[#1A1A1A]',
                            )}
                            disabled={!canGoToNextApproval || isSubmitting}
                            onClick={onNextApproval}
                            aria-label="Next approval"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                ) : null}
            </div>
            <div
                className={cn(
                    'grid grid-cols-1 gap-2 text-xs sm:grid-cols-3',
                    usesMinimalPresentation ? 'px-4 py-3' : 'mt-3',
                )}
            >
                <div>
                    <div className="text-muted-foreground font-medium">
                        Targets
                    </div>
                    <div>{targetCount}</div>
                </div>
                <div>
                    <div className="text-muted-foreground font-medium">
                        Mode
                    </div>
                    <div>{formatApprovalGroupMode(group.mode)}</div>
                </div>
                <div>
                    <div className="text-muted-foreground font-medium">
                        Risk
                    </div>
                    <div>{group.riskSummary}</div>
                </div>
            </div>
            <div
                className={cn(
                    'border-t',
                    usesMinimalPresentation
                        ? 'border-[#E5E5E5] px-4 py-3'
                        : 'border-border/50 mt-3 pt-3',
                )}
            >
                <div className="text-muted-foreground mb-2 text-xs font-medium">
                    Targets and changes
                </div>
                <div className="space-y-2">
                    {visibleTargets.map((target) => (
                        <div
                            key={target.commandId}
                            className="min-w-0 border-t border-[#EDEDED] pt-2 first:border-t-0 first:pt-0"
                        >
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                                <span className="min-w-0 text-sm font-medium text-[#1A1A1A]">
                                    {target.displayName}
                                </span>
                                <span className="text-muted-foreground text-[11px] break-all">
                                    {target.organizationId}
                                </span>
                            </div>
                            <div className="mt-1 space-y-1">
                                {target.changes.map((change) => (
                                    <div
                                        key={change.key}
                                        className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-xs"
                                    >
                                        <span className="min-w-0 truncate font-medium">
                                            {change.key}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {formatApprovalGroupValue(
                                                change.before,
                                            )}{' '}
                                            →{' '}
                                            {formatApprovalGroupValue(
                                                change.after,
                                            )}
                                        </span>
                                        <span
                                            className={cn(
                                                'text-right',
                                                change.changed
                                                    ? 'text-amber-700 dark:text-amber-400'
                                                    : 'text-muted-foreground',
                                            )}
                                        >
                                            {change.changed
                                                ? 'will change'
                                                : 'no change'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                {hiddenTargetCount > 0 || showAllTargets ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="mt-2 h-7 px-2 text-xs"
                        onClick={() => setShowAllTargets((current) => !current)}
                    >
                        <ChevronDown
                            className={cn(
                                'mr-1 h-3.5 w-3.5 transition-transform',
                                showAllTargets && 'rotate-180',
                            )}
                        />
                        {showAllTargets
                            ? 'Show fewer targets'
                            : `Show ${hiddenTargetCount} more`}
                    </Button>
                ) : null}
            </div>
            <div
                className={cn(
                    'flex flex-wrap items-center gap-2',
                    usesMinimalPresentation
                        ? 'justify-between border-t border-[#E5E5E5] px-4 py-3'
                        : 'mt-3 justify-end pt-1',
                )}
            >
                {usesMinimalPresentation ? (
                    <span className="text-[11px] font-medium text-[#8A8A8A]">
                        One decision applies to the full batch
                    </span>
                ) : null}
                <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className={cn(
                            usesMinimalPresentation &&
                                'h-8 gap-1.5 rounded-[6px] border-[#DCDCDC] bg-white px-2.5 text-xs font-medium text-[#1A1A1A] shadow-none hover:bg-[#F5F5F5] hover:text-[#1A1A1A]',
                        )}
                        onClick={() => {
                            if (onDecision) {
                                void onDecision('deny');
                            }
                        }}
                        disabled={isSubmitting || !canSubmit}
                    >
                        {usesMinimalPresentation ? (
                            <X className="size-3.5" />
                        ) : null}
                        Deny batch
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        variant="default"
                        className={cn(
                            usesMinimalPresentation &&
                                'h-8 gap-1.5 rounded-[6px] bg-[#1A1A1A] px-3 text-xs font-medium text-white shadow-none hover:bg-black',
                        )}
                        onClick={() => {
                            if (onDecision) {
                                void onDecision('allow_once');
                            }
                        }}
                        disabled={isSubmitting || !canSubmit}
                    >
                        {isSubmitting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Fragment>
                                {usesMinimalPresentation ? (
                                    <Check className="size-3.5" />
                                ) : null}
                                Approve batch
                            </Fragment>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );

    if (usesMinimalPresentation) {
        return (
            <div className="ml-7 flex w-[calc(100%-1.75rem)] max-w-full justify-start">
                {card}
            </div>
        );
    }

    return <div className="flex justify-start">{card}</div>;
}
